import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MenuItem, TextField } from '@mui/material';
import { DatePicker as MuiDatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment, { Moment } from 'moment';

import {
    getEarliestMoveInDate,
    getMaximumStayDuration,
} from 'src/utils/validators/validate-stay-selection';

import { styles } from './styles';

const getAvailableDurations = (
    moveInDate: string | null,
    moveOutDate: string,
    minimumStayDuration: number
) => {
    if (!moveInDate) {
        return [];
    }

    const parsedMoveInDate = moment(moveInDate, 'YYYY-MM-DD', true);
    const parsedMoveOutDate = moment(moveOutDate);

    if (
        !parsedMoveInDate.isValid() ||
        !parsedMoveOutDate.isValid() ||
        parsedMoveInDate.isBefore(getEarliestMoveInDate(), 'day')
    ) {
        return [];
    }

    const maximumDuration = getMaximumStayDuration(parsedMoveInDate, parsedMoveOutDate);

    if (maximumDuration < minimumStayDuration) {
        return [];
    }

    return Array.from(
        {
            length: maximumDuration - minimumStayDuration + 1,
        },
        (_, index) => minimumStayDuration + index
    );
};

interface DatePickerProps {
    minimumStayDuration: number;
    moveOutDate: string;
    moveInDate: string | null;
    stayDuration: number | null;
    onMoveInDateChange: (date: string | null) => void;
    onStayDurationChange: (duration: number | null) => void;
    onDurationValidityChange: (isValid: boolean) => void;
    onError: (message: string | null) => void;
}

export const DatePicker: FC<DatePickerProps> = ({
    minimumStayDuration,
    moveOutDate,
    moveInDate,
    stayDuration,
    onMoveInDateChange,
    onStayDurationChange,
    onDurationValidityChange,
    onError,
}) => {
    const { t } = useTranslation();
    const earliestMoveInDate = getEarliestMoveInDate();
    const maximumMoveInDate = useMemo(
        () => moment(moveOutDate).subtract(minimumStayDuration, 'week'),
        [moveOutDate, minimumStayDuration]
    );

    const availableDurations = useMemo(
        () => getAvailableDurations(moveInDate, moveOutDate, minimumStayDuration),
        [moveInDate, moveOutDate, minimumStayDuration]
    );

    const handleMoveInDateChange = (date: Moment | null) => {
        if (!date) {
            onMoveInDateChange(null);
            onStayDurationChange(null);
            onDurationValidityChange(false);
            onError(null);
            return;
        }

        const newMoveInDate = date.format('YYYY-MM-DD');

        const newAvailableDurations = getAvailableDurations(
            newMoveInDate,
            moveOutDate,
            minimumStayDuration
        );

        const isDurationValid =
            stayDuration === null || newAvailableDurations.includes(stayDuration);

        onMoveInDateChange(newMoveInDate);

        if (!isDurationValid) {
            onStayDurationChange(null);
        }

        onDurationValidityChange(isDurationValid);
        onError(null);
    };

    const handleMoveInDateBlur = () => {
        if (!moveInDate) {
            return;
        }

        const parsedMoveInDate = moment(moveInDate, 'YYYY-MM-DD', true);

        const isInvalidMoveInDate =
            !parsedMoveInDate.isValid() ||
            parsedMoveInDate.isBefore(earliestMoveInDate, 'day') ||
            parsedMoveInDate.isAfter(maximumMoveInDate, 'day');

        if (isInvalidMoveInDate) {
            onMoveInDateChange(null);
            onStayDurationChange(null);
            onDurationValidityChange(false);
            onError(t('rooms_page_error_invalid_move_in_date'));
        }
    };

    const handleStayDurationChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const duration = Number(event.target.value);

        if (!availableDurations.includes(duration)) {
            onStayDurationChange(null);
            onDurationValidityChange(false);
            return;
        }

        onStayDurationChange(duration);
        onDurationValidityChange(true);
        onError(null);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterMoment}>
            <div css={styles.container}>
                <MuiDatePicker
                    label={t('rooms_page_move_in_date')}
                    value={moveInDate ? moment(moveInDate, 'YYYY-MM-DD', true) : null}
                    minDate={earliestMoveInDate}
                    maxDate={maximumMoveInDate}
                    format="DD/MM/YYYY"
                    onChange={handleMoveInDateChange}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            placeholder: t('rooms_page_move_in_date'),
                            sx: styles.field,
                            InputLabelProps: {
                                shrink: true,
                            },
                            onBlur: handleMoveInDateBlur,
                        },
                        desktopPaper: { sx: styles.calendarPaper },
                        mobilePaper: { sx: styles.calendarPaper },
                    }}
                />

                <TextField
                    select
                    fullWidth
                    label={t('rooms_page_stay_duration')}
                    value={stayDuration ?? ''}
                    disabled={!moveInDate}
                    onChange={handleStayDurationChange}
                    sx={[styles.field, styles.durationField]}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    SelectProps={{
                        displayEmpty: true,
                        renderValue: (selected) => {
                            if (selected === '') {
                                return (
                                    <span css={styles.placeholder}>
                                        {t('rooms_page_stay_duration')}
                                    </span>
                                );
                            }

                            return `${selected} ${t('rooms_page_stay_duration_weeks')}`;
                        },
                    }}
                >
                    {availableDurations.map((duration) => (
                        <MenuItem key={duration} value={duration}>
                            {duration} {t('rooms_page_stay_duration_weeks')}
                        </MenuItem>
                    ))}
                </TextField>
            </div>
        </LocalizationProvider>
    );
};
